import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { IMediaAssetModel, resolvePreferredVideoHeight } from "../../Services/MediaAssetService";
import VideoPlayer, { VideoPlayerSource } from "./VideoPlayer";
import { resolveVideoPlayerSources } from "./resolveVideoPlayerSources";
import "./VideoPlayer.scss";

interface DeferredVideoPlayerProps {
    asset: IMediaAssetModel;
    preferredQuality?: number;
}

/**
 * Poster still + play button until the reader presses play. The Plyr instance (and therefore the video
 * byte stream) is only created on that click, so opening an article never starts downloading video. The
 * poster is the asset's thumbnail (captured at upload, or the Transcoder sprite) — a JPEG, not a video byte.
 * The sources are fixed at the moment of the click: a media list that reloads in the background (fresh
 * signed URLs) must not restart a video that is playing.
 */
const DeferredVideoPlayer: React.FC<DeferredVideoPlayerProps> = ({ asset, preferredQuality }) => {
    const { t } = useTranslation();
    const [sources, setSources] = useState<VideoPlayerSource[] | null>(null);
    const [resolvedQuality, setResolvedQuality] = useState<number | undefined>(preferredQuality);
    const [loading, setLoading] = useState(false);

    const startPlayback = async () => {
        if (loading || sources) {
            return;
        }
        const resolved = resolveVideoPlayerSources(asset);
        if (resolved.length === 0) {
            toast.error(t("knowledgeBase.form.mediaResolutionNotReady") as string);
            return;
        }
        setLoading(true);
        try {
            const smallest = [...resolved].sort((a, b) => a.size - b.size)[0];
            setResolvedQuality(
                preferredQuality ?? (await resolvePreferredVideoHeight(resolved.map((source) => source.size), smallest.src))
            );
            setSources(resolved);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="knowledge-deferred-video">
            {sources ? (
                <VideoPlayer
                    sources={sources}
                    autoplay
                    preferredQuality={resolvedQuality}
                    poster={asset.thumbnailUrl}
                    style={{ width: "100%", height: "100%" }}
                />
            ) : (
                <button
                    type="button"
                    className="knowledge-deferred-video__play"
                    onClick={startPlayback}
                    disabled={loading}
                    aria-label={t("knowledgeBase.playVideo") as string}
                >
                    {asset.thumbnailUrl && <img className="knowledge-deferred-video__poster" src={asset.thumbnailUrl} alt="" />}
                    <span className="knowledge-deferred-video__play-icon">
                        <i className={loading ? "pi pi-spinner pi-spin" : "pi pi-play"} />
                    </span>
                </button>
            )}
        </div>
    );
};

export default DeferredVideoPlayer;
